/**
 * Research source helpers - the jurisdiction → category → INDEX tree.
 *
 * `get_available_sources` returns ONE ROW PER LIVE INDEX. Each row is a single
 * searchable index:
 *
 *   { source_identifier: "bulgaria-case_law-vks",   // the REAL index name
 *     source_type:       "jurisdiction_case",
 *     source_name:       "Supreme Court of Cassation (VKS)",
 *     jurisdiction:      "BG",  jurisdiction_name: "Bulgaria",
 *     category:          "case_law", category_label: "Case Law",
 *     canonical_source_type: "case", document_count: 40297 }
 *
 * `source_identifier` is the exact string retrieval targets, so a SELECTION IS
 * A SET OF INDEX NAMES - that is what makes "only VKS" or "SAC + VKS"
 * expressible, where the old per-category rows could only say "Bulgaria case
 * law" and let the engine re-derive the indexes.
 *
 * The country lives in `jurisdiction`, NOT in `source_identifier`.
 */

import { ResearchSource } from '../../store/actions/ChatAction';

/* ------------------------------------------------------------------ */
/*  Identity                                                           */
/* ------------------------------------------------------------------ */

/**
 * Stable identity for a source.
 *
 * Index names and datasource UUIDs are both globally unique, and the
 * identifier is exactly what the settings PUT round-trips, so it IS the key.
 */
export function sourceKey(source: Pick<ResearchSource, 'source_identifier'>): string {
  return source.source_identifier;
}

/** The selected index names, for O(1) membership checks while rendering. */
export function selectionSet(selected: ResearchSource[]): Set<string> {
  return new Set((selected || []).map(sourceKey));
}

export function isSourceSelected(source: ResearchSource, selected: ResearchSource[]): boolean {
  return selectionSet(selected).has(sourceKey(source));
}

/** Add or remove one index from a selection. */
export function toggleSource(source: ResearchSource, selected: ResearchSource[]): ResearchSource[] {
  return isSourceSelected(source, selected)
    ? selected.filter((s) => sourceKey(s) !== sourceKey(source))
    : [...selected, source];
}

/**
 * Add or remove a whole set of indexes at once - the select-all controls.
 *
 * Adding preserves the existing order and skips anything already selected, so
 * "select all" after picking one index does not duplicate or reorder it.
 */
export function setSourcesSelected(
  sources: ResearchSource[],
  selected: ResearchSource[],
  shouldSelect: boolean
): ResearchSource[] {
  const affected = new Set(sources.map(sourceKey));
  if (!shouldSelect) return selected.filter((s) => !affected.has(sourceKey(s)));
  const already = selectionSet(selected);
  return [...selected, ...sources.filter((s) => !already.has(sourceKey(s)))];
}

/** A datasource that is still indexing cannot be searched yet. */
export function isSourceSelectable(source: ResearchSource): boolean {
  if (source.source_type !== 'user_datasource') return true;
  if (!source.status) return true;
  return source.status === 'ready' || source.status === 'active';
}

/** Only what the settings PUT accepts - the rest is display data. */
export function toSourceSelection(
  sources: ResearchSource[]
): Array<Pick<ResearchSource, 'source_type' | 'source_identifier' | 'source_name'>> {
  return sources.map((s) => ({
    source_type: s.source_type,
    source_identifier: s.source_identifier,
    source_name: s.source_name || '',
  }));
}

/**
 * May the server's answer replace the selection on screen?
 *
 * Adoption is how a reopened chat gets its stored scope back, but it is also
 * what ran the moment a chat was CREATED - while the send that creates it was
 * still writing that scope - and it answered "no sources", wiping the
 * jurisdiction the user had ticked on the start screen as soon as the URL
 * changed to /research/<uuid>.
 *
 * So: an empty server answer never overwrites a selection we are holding.
 * That mirrors the write contract, where `sources: []` also means "leave the
 * selection alone" rather than "clear it". A non-empty answer is the truth
 * and always wins; an empty one against an empty selection changes nothing.
 */
export function shouldAdoptSources(
  remote: ResearchSource[] | null | undefined,
  local: ResearchSource[] | null | undefined
): boolean {
  if ((remote || []).length) return true;
  return !(local || []).length;
}

/**
 * The one jurisdiction a selection speaks for, or null.
 *
 * Scope is the set of indexes, not this - the backend derives retrieval from
 * the stored rows. This is only the session's `jurisdiction` OVERRIDE, which
 * exists to say which jurisdiction a multi-jurisdiction chat is really about.
 * A selection that spans two countries has no answer to that question, and a
 * user datasource carries no jurisdiction at all, so both give null and the
 * override is left to the user's own settings.
 */
export function soleJurisdiction(sources: ResearchSource[]): string | null {
  const codes = new Set(
    (sources || [])
      .map((s) => (s.jurisdiction || '').toUpperCase())
      .filter(Boolean)
  );
  if (codes.size !== 1) return null;
  return [...codes][0];
}

/* ------------------------------------------------------------------ */
/*  Tri-state selection                                                */
/* ------------------------------------------------------------------ */

export type SelectionState = 'none' | 'partial' | 'all';

/** How much of a group is selected - drives the indeterminate checkbox. */
export function groupSelectionState(
  sources: ResearchSource[],
  selected: ResearchSource[]
): SelectionState {
  if (!sources.length) return 'none';
  const set = selectionSet(selected);
  const hits = sources.reduce((n, s) => n + (set.has(sourceKey(s)) ? 1 : 0), 0);
  if (hits === 0) return 'none';
  return hits === sources.length ? 'all' : 'partial';
}

/* ------------------------------------------------------------------ */
/*  The tree                                                           */
/* ------------------------------------------------------------------ */

export interface CategoryGroup {
  /** Raw category folder, e.g. `case_law`. */
  category: string;
  /** Backend wording, e.g. "Case Law". */
  label: string;
  /** One entry per live index, in backend order. */
  indexes: ResearchSource[];
  documentCount: number;
}

export interface JurisdictionGroup {
  /** ISO code, e.g. "BG". */
  code: string;
  /** Friendly label, e.g. "Bulgaria". */
  name: string;
  categories: CategoryGroup[];
  /** Every index under this jurisdiction, flattened - for select-all. */
  indexes: ResearchSource[];
  documentCount: number;
}

/** The label for one category, preferring the backend's own wording. */
export function categoryLabel(source: ResearchSource): string {
  if (source.category_label) return source.category_label;
  if (source.category) {
    return source.category.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  }
  return source.source_name || source.source_type;
}

/**
 * Build jurisdiction → category → index, preserving backend order at every
 * level (the catalogue is already ordered; re-sorting would fight it).
 *
 * A row with no `jurisdiction` is skipped rather than grouped under a blank
 * heading - the country is required to place an index in the tree.
 */
export function buildSourceTree(sources: ResearchSource[]): JurisdictionGroup[] {
  const groups: JurisdictionGroup[] = [];
  const byCode = new Map<string, JurisdictionGroup>();
  const byCategory = new Map<string, CategoryGroup>();

  (sources || []).forEach((src) => {
    const code = src.jurisdiction;
    if (!code || !src.source_identifier) return;

    let group = byCode.get(code);
    if (!group) {
      group = {
        code,
        name: src.jurisdiction_name || code,
        categories: [],
        indexes: [],
        documentCount: 0,
      };
      byCode.set(code, group);
      groups.push(group);
    }

    const categoryId = src.category || 'other';
    const categoryKey = `${code}::${categoryId}`;
    let category = byCategory.get(categoryKey);
    if (!category) {
      category = {
        category: categoryId,
        label: categoryLabel(src),
        indexes: [],
        documentCount: 0,
      };
      byCategory.set(categoryKey, category);
      group.categories.push(category);
    }

    category.indexes.push(src);
    group.indexes.push(src);
    // A null count means counting failed, not zero - it just adds nothing.
    const count = src.document_count || 0;
    category.documentCount += count;
    group.documentCount += count;
  });

  return groups;
}

/* ------------------------------------------------------------------ */
/*  Formatting                                                         */
/* ------------------------------------------------------------------ */

/** Exact, grouped count for an index row - "40,297". */
/**
 * Filter a built tree by a free-text query.
 *
 * Matching is on jurisdiction, category and index name, and a match ANYWHERE
 * keeps the branch that leads to it: an index that matches keeps its category
 * and jurisdiction visible, so the user can still see what they are selecting
 * and the whole-jurisdiction / whole-category toggles still act on something
 * meaningful.
 *
 * A jurisdiction or category that matches by NAME keeps all of its children —
 * typing "Bulgaria" should show everything Bulgarian, not nothing.
 *
 * The returned groups' `indexes` and `documentCount` are recomputed from what
 * survived, so a select-all on a filtered view selects exactly what is shown.
 */
export function filterSourceTree(
  tree: JurisdictionGroup[],
  query: string
): JurisdictionGroup[] {
  const needle = query.trim().toLowerCase();
  if (!needle) return tree;

  const hit = (value: unknown) => String(value ?? '').toLowerCase().includes(needle);

  return tree
    .map((jurisdiction) => {
      const jurisdictionMatches = hit(jurisdiction.name) || hit(jurisdiction.code);

      const categories = jurisdiction.categories
        .map((category) => {
          // A matching jurisdiction or category keeps everything beneath it.
          if (jurisdictionMatches || hit(category.label) || hit(category.category)) {
            return category;
          }
          const indexes = category.indexes.filter(
            (index) =>
              hit(index.source_name) ||
              hit(index.source_identifier) ||
              hit(index.description)
          );
          if (indexes.length === 0) return null;
          return {
            ...category,
            indexes,
            documentCount: indexes.reduce((sum, i) => sum + (i.document_count || 0), 0),
          };
        })
        .filter((category): category is CategoryGroup => category !== null);

      if (categories.length === 0) return null;

      const indexes = categories.flatMap((category) => category.indexes);
      return {
        ...jurisdiction,
        categories,
        indexes,
        documentCount: indexes.reduce((sum, i) => sum + (i.document_count || 0), 0),
      };
    })
    .filter((jurisdiction): jurisdiction is JurisdictionGroup => jurisdiction !== null);
}

export function formatExactCount(count?: number | null): string {
  if (count === null || count === undefined) return '';
  return count.toLocaleString();
}

/** Compact count for a group header - "40K". */
export function formatDocumentCount(count?: number | null): string {
  if (!count) return '';
  if (count >= 1_000_000) return `${(count / 1_000_000).toFixed(1)}M`;
  if (count >= 1_000) return `${(count / 1_000).toFixed(0)}K`;
  return `${count}`;
}
